import { css } from '@emotion/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { PaymentRequestStatus } from '@anytag/graphql';
import { Button, DropdownMenu, UploadButton, Form } from '@anytag/shared-atoms';
import { useAllAvailableCountryOptions, useToggleState, useTranslateOptions } from '@anytag/shared-hooks';
import {
  WarningModal,
  UploadCsvModal,
  SelectorField,
  TextSearchField,
  RangeDatePickerField
} from '@anytag/shared-molecules';
import { SelectedRangeOption } from '@anytag/shared-types';
import { useDownloadPayments, useUploadPayment } from './hooks';
import { PaymentFilterFormKeys, PaymentFilterFormValues } from './schemaTypes';

export interface TemplateProps {
  onSubmit?: () => void;
  disabledDownload: boolean;
}

export const Template = ({ onSubmit, disabledDownload }: TemplateProps) => {
  const { t } = useTranslation();
  const { isAdmin } = useUserRoles();
  const uploadModal = useToggleState();
  const { uploading, handleUploadFile } = useUploadPayment();
  const { getValues } = useFormContext<PaymentFilterFormValues>();
  const { status: isOpening, toggleStatus: toggleWarningModal } = useToggleState();
  const values = getValues();
  const { downloading, downloadPaymentOptions, downloadPayments } = useDownloadPayments({ filter: values });
  const { allAvailableCountryOptions } = useAllAvailableCountryOptions();
  const statusOptions = useTranslateOptions([
    { label: 'Reviewing', value: PaymentRequestStatus.REVIEWING },
    { label: 'Paid', value: PaymentRequestStatus.PAID },
    { label: 'Failed', value: PaymentRequestStatus.FAILED }
  ]);

  return (
    <>
      <Form.Fields className="items-end">
        <TextSearchField<PaymentFilterFormKeys>
          name="keyword"
          title={t('Search.Keyword')}
          onSubmit={onSubmit}
          placeholder={t<string>('Search.PaymentFilter')}
        />

        <SelectorField<PaymentFilterFormKeys>
          title={t('Selector.Country')}
          name="countryId"
          onSubmit={onSubmit}
          className="max-w-47.5"
          options={allAvailableCountryOptions}
        />

        <SelectorField<PaymentFilterFormKeys>
          name="status"
          title={t('Selector.Status')}
          onSubmit={onSubmit}
          options={statusOptions}
          className="max-w-47.5"
        />

        <RangeDatePickerField<PaymentFilterFormKeys>
          onSubmit={onSubmit}
          title={t('Selector.Date Range')}
          name={{ startDate: 'startDate', endDate: 'endDate' }}
          css={css({ maxWidth: '250px', '& > div > div': { minWidth: '225px' } })}
          rangeList={[
            SelectedRangeOption.TODAY,
            SelectedRangeOption.LAST_SEVEN_DAYS,
            SelectedRangeOption.TODAY_TO_MONTH_AGO,
            SelectedRangeOption.THIS_MONTH,
            SelectedRangeOption.LAST_MONTH,
            SelectedRangeOption.CUSTOM
          ]}
        />

        {isAdmin && (
          <>
            <Form.Field className="flex-none">
              <DropdownMenu
                open={!values.countryId ? false : undefined} // prevent from open if countryId is not selected
                trigger={
                  <Button
                    variant="outline"
                    loading={downloading}
                    title={t('Button.Download')}
                    disabled={disabledDownload}
                    prefixIcon={{ icon: 'download', size: '14px' }}
                    className="min-w-33 uppercase"
                    onClick={(e) => {
                      toggleWarningModal();
                    }}
                  />
                }
                side="bottom"
                align="end"
                className="relative m-0 max-w-33"
                onSelectOption={(option) => {
                  downloadPayments(option.value);
                }}
                disabled={disabledDownload}
                options={downloadPaymentOptions}
              />
            </Form.Field>

            <WarningModal open={isOpening} onClose={toggleWarningModal} title={t('Dialog.Please select a country')} />
          </>
        )}

        <Form.Field className="flex-none">
          <UploadButton onClick={uploadModal.open} loading={uploading} />
        </Form.Field>
      </Form.Fields>

      <UploadCsvModal
        uploading={uploading}
        open={uploadModal.status}
        onClose={uploadModal.close}
        onCustomUpload={handleUploadFile}
        dropAreaDescription={t('drop the file and upload')}
        description={t('paymentsBulkUploadCsvDescription')}
        notes={[t('General.DragAndDropMaxSize', { maxSize: '10MB' }), t('General.DragAndDropMIME', { MIME: 'CSV' })]}
      />
    </>
  );
};
